import type { GiftContentType } from '@/api/types/gift';
import type { ProductDetailGiftEntrance, ProductDetailGiftPayload } from '@/api/types/product';
import { getGiftActivityType } from '@/utils/gift-activity';

type ProductDetailGiftInteractionReporter = (buttonName: string) => void;

export const PDP_GIFT_INTERACTION_NAMES = {
  entrance: 'Free Gift',
  rule: 'Free Gift Rule',
  tier: 'Free Gift Tier',
  image: 'Free Gift Image',
} as const;

export function reportProductDetailGiftInteraction(
  reporter: ProductDetailGiftInteractionReporter | undefined,
  interactionName: (typeof PDP_GIFT_INTERACTION_NAMES)[keyof typeof PDP_GIFT_INTERACTION_NAMES],
) {
  if (!reporter) return;
  reporter(interactionName);
}

export function createProductDetailGiftRequestGuard() {
  let latestRequestId = 0;

  return {
    start: () => {
      latestRequestId += 1;
      return latestRequestId;
    },
    isCurrent: (requestId: number) => requestId === latestRequestId,
  };
}

export function isProductDetailGiftDialogOpen<T>(
  openedGiftData: T | null,
  currentGiftData: T | null | undefined,
) {
  return openedGiftData !== null && openedGiftData === currentGiftData;
}

const getNonEmptyString = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const getStringOrNumber = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? value : undefined;

const getImages = (images?: string[]) =>
  (images || []).map(getNonEmptyString).filter((image): image is string => Boolean(image));

const isGlass = (value: unknown) => value === 1 || value === '1';

const formatEventPeriod = (startTime?: string | number, endTime?: string | number) => {
  if (!startTime || !endTime) return undefined;
  return `${startTime} - ${endTime}`;
};

const splitDescription = (description?: string) =>
  description
    ?.split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean) || [];

const getTierImage = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
) =>
  getNonEmptyString(tier.gift_thumbnail_image) ||
  getNonEmptyString(tier.thumbnail) ||
  getNonEmptyString(tier.gift_large_image) ||
  getNonEmptyString(tier.large_image);

const getTierLargeImage = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
) =>
  getNonEmptyString(tier.gift_large_image) ||
  getNonEmptyString(tier.large_image) ||
  getNonEmptyString(tier.gift_thumbnail_image) ||
  getNonEmptyString(tier.thumbnail);

const isTierSoldOut = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
) => {
  if (
    tier.remaining_qty !== undefined &&
    tier.remaining_qty !== null &&
    tier.remaining_qty !== ''
  ) {
    return Number(tier.remaining_qty) <= 0;
  }

  return tier.is_sold_out ?? tier.sold_out;
};

const getTierName = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
) => getNonEmptyString(tier.name) || getNonEmptyString(tier.gift_name);

const getTerms = (
  termsEntry?: ProductDetailGiftEntrance['terms_entry'],
  fallback?: { content?: string; contentType?: GiftContentType; label?: string },
) => {
  if (!termsEntry && !fallback?.content) return undefined;

  const content = termsEntry?.content || fallback?.content;
  return {
    show: termsEntry?.show ?? Boolean(content),
    label: termsEntry?.label || fallback?.label,
    ...(termsEntry?.label ? { title: termsEntry.label } : {}),
    content,
    contentType: termsEntry?.content_type || fallback?.contentType,
  };
};

const getTierDisclaimerFallback = (
  tiers: NonNullable<NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']>,
) => {
  const tier =
    tiers.find((item) => item.is_current === true && getNonEmptyString(item.disclaimer)) ||
    tiers.find((item) => getNonEmptyString(item.disclaimer));
  if (!tier) return undefined;

  return {
    content: getNonEmptyString(tier.disclaimer),
    contentType: tier.disclaimer_content_type,
  };
};

const getPurchaseGift = (
  activity: NonNullable<ProductDetailGiftPayload['detail_activities']>[number],
  activityIndex: number,
  images: string[],
) => {
  const source = activity.purchase_gift;
  if (!source || typeof source !== 'object') return undefined;

  const image = getNonEmptyString(source.thumbnail_image) || getNonEmptyString(source.image);
  const name = getNonEmptyString(source.name);
  if (!image || !name) return undefined;

  const id =
    getStringOrNumber(source.id) ?? `${activity.id ?? activity.activity_id ?? activityIndex}-gift`;
  const purchaseGiftImages = getImages(source.images);
  const activityImages = getImages(activity.images);
  const resolvedImages =
    purchaseGiftImages.length > 0
      ? purchaseGiftImages
      : activityImages.length > 0
        ? activityImages
        : images;
  const eventPeriod =
    getNonEmptyString(source.event_period) ||
    formatEventPeriod(activity.start_time, activity.end_time);
  const rules = Array.isArray(source.rules)
    ? source.rules.map(getNonEmptyString).filter((rule): rule is string => Boolean(rule))
    : undefined;

  return {
    id,
    name,
    image,
    isGlass: isGlass(source.is_glass),
    subtitle: getNonEmptyString(source.subtitle),
    thresholdText: getNonEmptyString(source.threshold_text),
    detail: {
      id,
      name,
      color: getNonEmptyString(source.color),
      images: resolvedImages.length > 0 ? resolvedImages : [image],
      isGlass: isGlass(source.is_glass),
      sellPrice: getStringOrNumber(source.sell_price),
      oldPrice: getStringOrNumber(source.old_price),
      stockText: getNonEmptyString(source.stock_text),
      comeWithText: getNonEmptyString(source.come_with_text),
      eventPeriod,
      rules,
    },
  };
};

const getSingleTierGift = (
  tier: NonNullable<
    NonNullable<ProductDetailGiftPayload['detail_activities']>[number]['tiers']
  >[number],
  activity: NonNullable<ProductDetailGiftPayload['detail_activities']>[number],
  activityIndex: number,
) => {
  const image = getTierImage(tier);
  const name = getTierName(tier);
  if (!image || !name) return undefined;

  const id =
    getStringOrNumber(tier.id) ??
    getStringOrNumber(tier.tier_id) ??
    getStringOrNumber(tier.stair_index) ??
    `${activity.id ?? activity.activity_id ?? activityIndex}-tier`;
  const largeImage = getTierLargeImage(tier) || image;
  const glassProps = isGlass(tier.is_glass) ? { isGlass: true as const } : {};

  return {
    id,
    name,
    image,
    ...glassProps,
    thresholdText: getNonEmptyString(tier.threshold_text),
    detail: {
      id,
      name,
      images: [largeImage],
      ...glassProps,
    },
  };
};

const mapActivity = (
  activity: NonNullable<ProductDetailGiftPayload['detail_activities']>[number],
  activityIndex: number,
  detailImages: string[],
) => {
  const eventPeriod = formatEventPeriod(activity.start_time, activity.end_time);
  const descriptionLines = splitDescription(activity.description);

  const tiers = (activity.tiers || []).map((tier, tierIndex) => ({
    id: tier.id ?? tier.tier_id ?? tier.stair_index ?? `${activityIndex}-${tierIndex}`,
    name: getTierName(tier) || '',
    thresholdAmount: tier.threshold_amount,
    thresholdText: tier.threshold_text,
    thumbnail: getTierImage(tier) || '',
    largeImage: getTierLargeImage(tier),
    soldOut: isTierSoldOut(tier),
  }));
  const tierTermsFallback = getTierDisclaimerFallback(activity.tiers || []);

  const purchaseGift = getPurchaseGift(activity, activityIndex, detailImages);
  const singleGift =
    tiers.length === 1
      ? getSingleTierGift(activity.tiers?.[0], activity, activityIndex)
      : tiers.length === 0
        ? purchaseGift
          ? { ...purchaseGift, subtitle: undefined }
          : undefined
        : undefined;
  const singleGiftWithDescription = singleGift
    ? {
        ...singleGift,
        detail: {
          ...singleGift.detail,
          ...(descriptionLines.length > 0 ? { descriptionLines } : {}),
        },
      }
    : undefined;
  const displayStyle = singleGiftWithDescription
    ? ('single-tier' as const)
    : tiers.length > 1
      ? ('multi-tier' as const)
      : undefined;

  return {
    id: activity.id ?? activity.activity_id ?? activityIndex,
    title: activity.title || '',
    ruleLabel: activity.rule_label,
    terms: getTerms(activity.terms_entry, {
      // 后端约定：活动级免责声明优先，阶梯级免责声明仅作为兜底。
      content: activity.disclaimer_content || tierTermsFallback?.content,
      contentType: activity.disclaimer_content_type || tierTermsFallback?.contentType,
      label: activity.rule_label,
    }),
    eventPeriod,
    descriptionLines: descriptionLines.length > 0 ? descriptionLines : undefined,
    tiers,
    ...(displayStyle
      ? {
          displayStyle,
          activityType: getGiftActivityType(activity),
          ...(singleGiftWithDescription ? { singleGift: singleGiftWithDescription } : {}),
        }
      : {}),
    ...(!displayStyle && purchaseGift
      ? {
          purchaseGiftTitle: getNonEmptyString(activity.purchase_gift_title),
          purchaseGift,
        }
      : {}),
  };
};

type DirectPurchaseGiftDetail = NonNullable<ReturnType<typeof getPurchaseGift>>['detail'] & {
  descriptionLines?: string[];
};

const getDirectSingleDialogGift = (
  sourceActivity: NonNullable<ProductDetailGiftPayload['detail_activities']>[number],
  mappedActivity: ReturnType<typeof mapActivity>,
) => {
  if (sourceActivity.tiers?.length || mappedActivity.displayStyle !== 'single-tier') {
    return undefined;
  }

  // With no tiers, mapActivity can only build singleGift from the API purchase_gift branch.
  const gift = mappedActivity.singleGift?.detail as DirectPurchaseGiftDetail | undefined;
  if (!gift) return undefined;

  return {
    ...gift,
    ...(mappedActivity.terms
      ? {
          rule: {
            ruleLabel: mappedActivity.ruleLabel,
            terms: mappedActivity.terms,
          },
        }
      : {}),
  };
};

export function buildProductDetailGiftPresentation(payload?: ProductDetailGiftPayload | null) {
  if (payload?.show !== true || !payload.entrance) return null;

  const entrance = payload.entrance;
  const entranceImage = getNonEmptyString(entrance?.gift_image);
  const title = getNonEmptyString(entrance.title) || getNonEmptyString(entrance.gift_name);

  if (!title || !entranceImage) return null;

  const entranceTerms = getTerms(entrance.terms_entry);
  const detailImages = getImages(payload.images);
  const detailActivities = (payload.detail_activities || []).map((activity, index) =>
    mapActivity(activity, index, detailImages),
  );
  const firstSourceActivity = payload.detail_activities?.[0];
  const directSingleDialogGift =
    detailActivities.length === 1 && firstSourceActivity
      ? getDirectSingleDialogGift(firstSourceActivity, detailActivities[0])
      : undefined;
  const teaserRules: string[] = [];

  return {
    teaser: {
      title,
      image: entranceImage,
      images: [entranceImage],
      isGlass: isGlass(entrance.is_glass),
      ruleText: getNonEmptyString(entrance.threshold_text),
      rules: teaserRules,
      terms: entranceTerms,
      tracking: entrance.tracking,
      isRandomGift: entrance.is_random_gift,
      giftName: title,
    },
    useDialogFlow: detailActivities.length > 0 && !directSingleDialogGift,
    dialogTitle: getNonEmptyString(entrance.dialog_title),
    dialogActivities: detailActivities,
    ...(directSingleDialogGift ? { singleDialogGift: directSingleDialogGift } : {}),
  };
}
